import { messagingApi } from '../../shared/utils/apiClient';

// ────────────────────────────────────────────────────────────────────────────
// Types mirroring messaging-service-go models/david_company_conversation.go
// ────────────────────────────────────────────────────────────────────────────

export interface CandidateRecommendation {
    candidate_id: string;
    name: string;
    headline?: string;
    source?: string;
    years_experience?: number;
    titles?: string[];
    companies?: string[];
    skills?: string[];
    locations?: string[];
    qwen_score?: number;
    vector_similarity?: number;
    match_score?: number;
    matched_skills?: string[];
    matched_certs?: string[];
    matched_titles?: string[];
    matched_keywords?: string[];
    justification?: string;
}

export interface MessageFeedback {
    type: 'thumbs_up' | 'thumbs_down';
    created_at: string;
}

export interface QuickResponse {
    text: string;
    created_at: string;
}

export interface DavidCompanyConversation {
    id: string;
    target_user_id: string;
    target_role: string;
    target_name?: string;
    company_id?: string;
    company_name?: string;
    last_message?: {
        content: string;
        sender_id: string;
        timestamp: string;
    };
    created_at: string;
    updated_at: string;
}

export interface DavidCompanyMessage {
    id: string;
    conversation_id: string;
    sender_type: 'david_bot' | 'company' | string;
    sender_name: string;
    content: string;
    message_type: 'text' | 'candidate_recommendation' | string;
    candidate_recommendations?: CandidateRecommendation[];
    recruiter_query?: string;
    structured_query?: Record<string, unknown>;
    original_draft?: string;
    feedback?: MessageFeedback | null;
    quick_response?: QuickResponse | null;
    created_at: string;
}

// ────────────────────────────────────────────────────────────────────────────
// API calls — scoped to the authenticated company user's own conversations
// ────────────────────────────────────────────────────────────────────────────

export async function listMyDavidConversations(): Promise<DavidCompanyConversation[]> {
    const { conversations } = await messagingApi.get<{
        conversations: DavidCompanyConversation[];
    }>('/david/company-conversations');
    return conversations ?? [];
}

export async function getMyDavidMessages(conversationId: string): Promise<DavidCompanyMessage[]> {
    const { messages } = await messagingApi.get<{
        messages: DavidCompanyMessage[];
    }>(`/david/company-messages?conversation_id=${encodeURIComponent(conversationId)}`);
    return messages ?? [];
}

export async function sendFeedback(
    messageId: string,
    type: 'thumbs_up' | 'thumbs_down'
): Promise<void> {
    await messagingApi.post('/david/company-messages/feedback', {
        message_id: messageId,
        type,
    });
}

export async function sendQuickResponse(
    messageId: string,
    text: string
): Promise<void> {
    await messagingApi.post('/david/company-messages/quick-response', {
        message_id: messageId,
        text,
    });
}
